import AddIcon from "@mui/icons-material/Add";
import { Button, Divider, Grid, TextField, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import * as React from "react";
import { useState } from "react";
import { useRecordContext } from "react-admin";
import { Controller, useForm } from "react-hook-form";
import CustomEditRemoveIcon from "../../unit/CustomEditRemoveIcon";
import CustomProductTextField from "../../unit/forms/CustomProductTextField";
import useNotify from "../../unit/useNotify";
import SectionStyled from "./SectionStyled";
import SectionTitle from "./SectionTitle";
import CustomProductActionBottom from "../CustomProductActionBottom";

export default function AuthorizationDisbursement({
  onSave,
  defaultValues,
  ...props
}: any) {
  const itemKeyRef = React.useRef(0);
  const record = useRecordContext();
  const { success, error, warning, info } = useNotify();
  const sectionName = "authorization-disbursement";
  const sectionTitle =
    "Authorization and Acknowledgement - Information that requires customer's review or approval";
  const sectionSubtitle = "";

  const defaultAuthorizationDisbursement = [
    {
      id: "",
      title: "",
      description: "",
    },
  ];

  const [authorizationDisbursement, setAuthorizationDisbursement] = useState(
    (
      record?.data?.basic?.authorization?.disbursement_disclosure ||
      defaultAuthorizationDisbursement
    ).map((item: any) => ({
      ...item,
      __clientKey: `authorization-disbursement-${itemKeyRef.current++}`,
    }))
  );
  const subType = record?.data?.basic?.subType;
  const division = record?.data?.basic?.division;
  const productModulesGroup = record?.data?.basic?.productModulesGroup;

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    getValues,
    setValue,
  } = useForm({
    mode: "onBlur",
    defaultValues: defaultValues || {},
  });

  React.useEffect(() => {
    if (record?.data?.basic?.authorization) {
      const nextAuthorizationDisbursement = (
        record.data.basic.authorization.disbursement_disclosure ||
        defaultAuthorizationDisbursement
      ).map((item: any) => ({
        ...item,
        __clientKey: `authorization-disbursement-${itemKeyRef.current++}`,
      }));

      setAuthorizationDisbursement(nextAuthorizationDisbursement);
      reset({
        authorizationDisbursement: nextAuthorizationDisbursement,
      });
    }
  }, [record, reset]);
  React.useEffect(() => {
    if (props?.onDirtyChange) {
      props?.onDirtyChange(props?.curMenu, isDirty);
    }
  }, [isDirty, reset]);

  const hasEmptyAuthorizationDisbursement = authorizationDisbursement.some(
    (item: any) => !item.title?.trim() || !item.description?.trim()
  );

  const buildDefaultAuthorizationDisbursement = () =>
    defaultAuthorizationDisbursement.map((item) => ({
      ...item,
      __clientKey: `authorization-disbursement-${itemKeyRef.current++}`,
    }));

  const sanitizeAuthorizationDisbursement = (items: any[]) =>
    items.map(({ __clientKey, ...item }) => item);

  const persistAuthorizationDisbursement = (
    nextAuthorizationDisbursement: any[]
  ) => {
    const formData: any = getValues();
    formData.saveAction = "authorization";
    formData.saveActionType = "disbursementDisclosure";
    formData.authorizationDisbursement = sanitizeAuthorizationDisbursement(
      nextAuthorizationDisbursement
    );

    if (onSave) {
      onSave(formData, props?.curMenu);
      reset({
        ...formData,
        authorizationDisbursement: nextAuthorizationDisbursement,
      });
    }
  };

  const addAuthorizationDisbursement = () => {
    const newAuthorizationDisbursement = {
      id: "",
      title: "",
      description: "",
      __clientKey: `authorization-disbursement-${itemKeyRef.current++}`,
    };
    const nextAuthorizationDisbursement = [
      ...authorizationDisbursement,
      newAuthorizationDisbursement,
    ];

    setAuthorizationDisbursement(nextAuthorizationDisbursement);
    setValue("authorizationDisbursement", nextAuthorizationDisbursement, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const addDefaultAuthorizationDisbursement = () => {
    const nextAuthorizationDisbursement = [
      ...authorizationDisbursement,
      ...buildDefaultAuthorizationDisbursement(),
    ];
    setAuthorizationDisbursement(nextAuthorizationDisbursement);
    setValue("authorizationDisbursement", nextAuthorizationDisbursement, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const removeAcknowledgement = (index: any) => {
    if (authorizationDisbursement.length > 0) {
      const updated = [...authorizationDisbursement];
      updated.splice(index, 1);
      setAuthorizationDisbursement(updated);
      setValue("authorizationDisbursement", updated, { shouldDirty: true });
      persistAuthorizationDisbursement(updated);
    }
    success("Acknowledgement removed and saved.");
  };

  const handleSaveClick = () => {
    if (hasEmptyAuthorizationDisbursement) {
      warning("Title and Description cannot be empty.");
      return;
    }

    persistAuthorizationDisbursement(authorizationDisbursement);
  };

  const handleAuthorizationDisbursementChange = (
    index: any,
    field: any,
    value: any
  ) => {
    const updated = [...authorizationDisbursement];
    updated[index][field] = value;
    setAuthorizationDisbursement(updated);
    setValue(`authorizationDisbursement[${index}].${field}`, value, {
      shouldDirty: true,
    });
  };

  return (
    <SectionStyled id={"section_" + sectionName}>
      <SectionTitle subtitle={sectionSubtitle} title={sectionTitle} />

      <Box
        sx={{
          padding: "10px",
          height: "calc(100vh - 255px)",
          overflowY: "auto",
          // background: "yellow",
        }}
      >
        {productModulesGroup?.includes("disclosure") && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h3" sx={{ mb: 2 }}>
                Disbursement
              </Typography>
              {authorizationDisbursement.map((item: any, index: any) => (
                <React.Fragment key={item.__clientKey}>
                  <Grid
                    container
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 3 }}
                  >
                    <Grid item xs={10}>
                      <Controller
                        name={`authorizationDisbursement[${index}].title`}
                        control={control}
                        render={({ field }) => (
                          <CustomProductTextField
                            {...field}
                            fullWidth
                            variant="outlined"
                            label="Title"
                            value={item.title}
                            onChange={(e) => {
                              field.onChange(e);
                              handleAuthorizationDisbursementChange(
                                index,
                                "title",
                                e.target.value
                              );
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <TextField
                        type="hidden"
                        name={`authorizationDisbursement[${index}].id`}
                        value={item.id}
                        sx={{ display: "none" }}
                      />
                      {authorizationDisbursement.length > 0 && (
                        <CustomEditRemoveIcon
                          onClick={() => removeAcknowledgement(index)}
                          tooltip="Remove this acknowledgement"
                        />
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name={`authorizationDisbursement[${index}].description`}
                        control={control}
                        render={({ field }) => (
                          <CustomProductTextField
                            {...field}
                            fullWidth
                            multiline
                            rows={4}
                            variant="outlined"
                            label="Description"
                            value={item.description}
                            onChange={(e) => {
                              field.onChange(e);
                              handleAuthorizationDisbursementChange(
                                index,
                                "description",
                                e.target.value
                              );
                            }}
                            sx={{ marginTop: "-4px" }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  {index < authorizationDisbursement.length - 1 && (
                    <Divider sx={{ my: 2 }} />
                  )}
                </React.Fragment>
              ))}
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addAuthorizationDisbursement}
                sx={{ width: "120px" }}
              >
                Add
              </Button>
              {/* <Button
                variant="outlined"
                onClick={addDefaultAuthorizationDisbursement}
                sx={{ width: "140px", ml: 2 }}
              >
                Use Default
              </Button> */}
            </Grid>
          </Grid>
        )}
      </Box>
      {/* Form Actions */}
      <CustomProductActionBottom>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveClick}
          disabled={!isDirty || hasEmptyAuthorizationDisbursement}
          sx={{ width: "120px", mt: 1 }}
        >
          Save
        </Button>
      </CustomProductActionBottom>
    </SectionStyled>
  );
}
